import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import cx from 'classnames';
import {ComponentProps, useEffect, useRef, useState} from 'react';

import styles from './WorkstreamSidebar.module.css';

interface SidebarOverflowTooltipProps {
  className?: string;
  color?: ComponentProps<typeof Text>['color'];
  isBold?: boolean;
  placement?: React.ComponentProps<typeof Tooltip.Control>['placement'];
  size?: ComponentProps<typeof Text>['size'];
  text: string;
}

const TOOLTIP_OFFSET_DISTANCE = 6;

export const SidebarOverflowTooltip = ({
  className,
  color,
  isBold,
  placement = 'right',
  size,
  text,
}: SidebarOverflowTooltipProps) => {
  const labelRef = useRef<HTMLDivElement>(null);
  const tooltipTriggerRef = useRef<HTMLDivElement>(null) as {current: HTMLDivElement | null};
  const [isTruncated, setIsTruncated] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const element = labelRef.current;
    if (!element) return;
    const nearestAnchor = element.closest('[data-sidebar-tooltip-anchor]') as HTMLDivElement | null;
    tooltipTriggerRef.current = nearestAnchor ?? element;

    const updateTruncation = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth + 1);
    };

    const raf = window.requestAnimationFrame(updateTruncation);
    if (typeof ResizeObserver === 'undefined') {
      return () => window.cancelAnimationFrame(raf);
    }

    const observer = new ResizeObserver(updateTruncation);
    observer.observe(element);
    return () => {
      window.cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [text]);

  const isTooltipOpen = isHovered && isTruncated;

  return (
    <>
      {isTruncated && (
        <Tooltip.Control
          open={isTooltipOpen}
          triggerRef={tooltipTriggerRef}
          placement={placement}
          offsetDistance={TOOLTIP_OFFSET_DISTANCE}
        >
          {text}
        </Tooltip.Control>
      )}
      <div
        ref={labelRef}
        className={cx(styles.sidebarOverflowLabel, className)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <span className={styles.sidebarOverflowLabelAnchor}>
          <Text color={color} isBold={isBold} size={size}>
            {text}
          </Text>
        </span>
      </div>
    </>
  );
};
